// Simple SEO helper that updates title, meta description and canonical link
// Set your canonical host here. All canonical and absolute OG/twitter image URLs
// will be normalized to this host so search engines see a single canonical domain.
const CANONICAL_HOST = 'https://www.aakeeo.com';

export function setSeo({ title, description, canonical, og = {}, twitter = {}, jsonLd, keywords } = {}) {
  // Title
  if (title) document.title = title;

  // Description
  if (description) {
    let desc = document.querySelector('meta[name="description"]');
    if (!desc) {
      desc = document.createElement('meta');
      desc.setAttribute('name', 'description');
      document.head.appendChild(desc);
    }
    desc.setAttribute('content', description);
  }

  // Keywords
  if (keywords) {
    let keywordsMeta = document.querySelector('meta[name="keywords"]');
    if (!keywordsMeta) {
      keywordsMeta = document.createElement('meta');
      keywordsMeta.setAttribute('name', 'keywords');
      document.head.appendChild(keywordsMeta);
    }
    keywordsMeta.setAttribute('content', Array.isArray(keywords) ? keywords.join(', ') : keywords);
  }

  // Canonical: always normalize to CANONICAL_HOST so prerender doesn't leak
  // localhost URLs. If a caller passes a full localhost URL (e.g. via
  // window.location.href), swap its origin for CANONICAL_HOST.
  try {
    let canonicalUrl;
    if (canonical && (canonical.startsWith('http://') || canonical.startsWith('https://'))) {
      try {
        const parsed = new URL(canonical);
        canonicalUrl =
          parsed.host === 'www.aakeeo.com'
            ? canonical
            : CANONICAL_HOST + parsed.pathname + parsed.search;
      } catch (err) {
        canonicalUrl = CANONICAL_HOST + window.location.pathname + window.location.search;
      }
    } else {
      canonicalUrl = CANONICAL_HOST + window.location.pathname + window.location.search;
    }
    let link = document.querySelector('link[rel="canonical"]');
    if (!link) {
      link = document.createElement('link');
      link.setAttribute('rel', 'canonical');
      document.head.appendChild(link);
    }
    link.setAttribute('href', canonicalUrl);
  } catch (e) {
    // ignore in environments without window
  }

  // Open Graph tags
  if (og && typeof og === 'object') {
    const setOg = (property, content) => {
      if (!content) return;
      // Normalize image and url values to canonical host when needed
      let final = content;
      try {
        // If content begins with a single slash, prefix the canonical host
        if (typeof content === 'string' && content.startsWith('/')) {
          final = CANONICAL_HOST + content;
        } else if (typeof content === 'string' && content.startsWith(window.location.origin)) {
          // Replace current origin with canonical host
          final = content.replace(window.location.origin, CANONICAL_HOST);
        }
      } catch (e) {
        // ignore (window may be undefined in some environments)
      }

      let el = document.querySelector(`meta[property="${property}"]`);
      if (!el) {
        el = document.createElement('meta');
        el.setAttribute('property', property);
        document.head.appendChild(el);
      }
      el.setAttribute('content', final);
    };

    setOg('og:title', og.title || title);
    setOg('og:description', og.description || description);
    setOg('og:url', og.url || CANONICAL_HOST + window.location.pathname + window.location.search);
    setOg('og:type', og.type || 'website');
    if (og.image) setOg('og:image', og.image);
  }

  // Twitter Card tags
  if (twitter && typeof twitter === 'object') {
    const setTwitter = (name, content) => {
      if (!content) return;
      let final = content;
      try {
        if (typeof content === 'string' && content.startsWith('/')) {
          final = CANONICAL_HOST + content;
        } else if (typeof content === 'string' && content.startsWith(window.location.origin)) {
          final = content.replace(window.location.origin, CANONICAL_HOST);
        }
      } catch (e) {
        // ignore
      }

      let el = document.querySelector(`meta[name="${name}"]`);
      if (!el) {
        el = document.createElement('meta');
        el.setAttribute('name', name);
        document.head.appendChild(el);
      }
      el.setAttribute('content', final);
    };

    setTwitter('twitter:card', twitter.card || (og && og.image ? 'summary_large_image' : 'summary'));
    setTwitter('twitter:title', twitter.title || title);
    setTwitter('twitter:description', twitter.description || description);
    if (twitter.image || (og && og.image)) setTwitter('twitter:image', twitter.image || og.image);
  }

  // JSON-LD injection (supports single object or array of schemas)
  if (jsonLd) {
    try {
      // Remove existing helper-generated schemas so we can re-inject cleanly
      document
        .querySelectorAll('script[type="application/ld+json"][data-generated-by="seo-helper"]')
        .forEach((el) => el.remove());

      // Walk the schema and swap any current-origin URLs (e.g. localhost during
      // prerender) for CANONICAL_HOST. Callers often build URLs with
      // `${window.location.origin}/...` which leaks `http://localhost:4174` into
      // BlogPosting `url`/`mainEntityOfPage` when Puppeteer prerenders dist/.
      const localOrigin =
        (typeof window !== 'undefined' && window.location && window.location.origin) || '';
      const normalize = (val) => {
        if (typeof val === 'string') {
          if (localOrigin && localOrigin !== CANONICAL_HOST && val.startsWith(localOrigin)) {
            return CANONICAL_HOST + val.slice(localOrigin.length);
          }
          return val;
        }
        if (Array.isArray(val)) return val.map(normalize);
        if (val && typeof val === 'object') {
          const out = {};
          for (const k of Object.keys(val)) out[k] = normalize(val[k]);
          return out;
        }
        return val;
      };

      const schemas = Array.isArray(jsonLd) ? jsonLd : [jsonLd];
      schemas.forEach((schema) => {
        const script = document.createElement('script');
        script.setAttribute('type', 'application/ld+json');
        script.setAttribute('data-generated-by', 'seo-helper');
        script.textContent = JSON.stringify(normalize(schema));
        document.head.appendChild(script);
      });
    } catch (e) {
      // If serialization fails, ignore silently
      // eslint-disable-next-line no-console
      console.warn('Failed to inject JSON-LD', e);
    }
  }
}

export default setSeo;
